// ==============================================
// Scout sign-in page (/auth/scout)
// ==============================================
//
// A Scout's web view opens this, with a one-time link the Scout asked the API
// for, before showing a MyPolaris page: it signs the Scout's owner in and moves
// on to the page they wanted. Unlike the emailed-link pages it runs on load:
// the link is never emailed, so no mail scanner can reach it.
//
// Whatever happens (spent link, a sign-in already here, no network) it moves on
// to the page, which shows the sign-in screen if nobody is signed in. It never
// strands anyone here.
//
// @docs:
//   - docs/core/architecture/SCOUT_MYPOLARIS_SIGN_IN.md

import { useEffect, useRef } from 'react';
import { scoutSignIn } from '../../lib/auth-client';
import { leaveTo, parseScoutLink } from '../../lib/scoutLink';

export function ScoutSignInPage() {
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // StrictMode runs effects twice in development; the link works once.
    started.current = true;
    const { token, next } = parseScoutLink(window.location.hash);
    // The token never stays in the address bar or the history.
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) {
      leaveTo(next);
      return;
    }
    void scoutSignIn(token)
      .catch(() => undefined)
      .finally(() => leaveTo(next));
  }, []);

  return (
    <div
      className="flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4"
      style={{ height: 'var(--vortex-usable-height, 100vh)' }}
    >
      <div className="flex flex-col items-center gap-4" role="status" aria-live="polite">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-base text-slate-300">Opening MyPolaris…</p>
      </div>
    </div>
  );
}

export default ScoutSignInPage;
